import React, { useState, useEffect } from 'react'
import { Save, Edit2, X } from 'lucide-react'
import { Button, Card, Input } from '../components/common'
import { CATEGORIES } from '../config/categories'

export default function AIPromptsPage() {
  const [prompts, setPrompts] = useState({})
  const [editingCategory, setEditingCategory] = useState(null)
  const [editedPrompt, setEditedPrompt] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    // Load prompts from localStorage or use defaults
    const savedPrompts = localStorage.getItem('aiPrompts')
    if (savedPrompts) {
      try {
        const parsed = JSON.parse(savedPrompts)
        setPrompts(parsed)
      } catch (e) {
        // If parsing fails, use defaults
        initializeDefaultPrompts()
      }
    } else {
      initializeDefaultPrompts()
    }
  }, [])

  const initializeDefaultPrompts = () => {
    const defaultPrompts = {}
    Object.entries(CATEGORIES).forEach(([code, category]) => {
      defaultPrompts[code] = category.aiPrompt
    })
    setPrompts(defaultPrompts)
  }

  const handleEdit = (categoryCode) => {
    setEditingCategory(categoryCode)
    setEditedPrompt(prompts[categoryCode] || CATEGORIES[categoryCode].aiPrompt)
  }

  const handleSave = (categoryCode) => {
    const updatedPrompts = {
      ...prompts,
      [categoryCode]: editedPrompt
    }
    setPrompts(updatedPrompts)
    localStorage.setItem('aiPrompts', JSON.stringify(updatedPrompts))
    setEditingCategory(null)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const handleCancel = () => {
    setEditingCategory(null)
    setEditedPrompt('')
  }

  const handleReset = (categoryCode) => {
    if (confirm(`Reset ${categoryCode} prompt to default?`)) {
      const updatedPrompts = {
        ...prompts,
        [categoryCode]: CATEGORIES[categoryCode].aiPrompt
      }
      setPrompts(updatedPrompts)
      localStorage.setItem('aiPrompts', JSON.stringify(updatedPrompts))
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    }
  }

  const handleResetAll = () => {
    if (confirm('Reset ALL prompts to defaults? This cannot be undone.')) {
      initializeDefaultPrompts()
      localStorage.removeItem('aiPrompts')
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    }
  }

  // Group categories by section
  const sections = {
    D: { name: 'Section D: Outside the property', categories: [] },
    E: { name: 'Section E: Inside the property', categories: [] },
    F: { name: 'Section F: Services', categories: [] },
    G: { name: 'Section G: Grounds', categories: [] }
  }

  Object.entries(CATEGORIES).forEach(([code, category]) => {
    sections[category.section].categories.push({ code, ...category })
  })

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Prompts Configuration</h1>
          <p className="text-sm text-slate-600 mt-1">
            Customize the OpenAI analysis prompts for each category
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="text-green-600 text-sm flex items-center gap-1">
              <Save className="w-4 h-4" />
              Saved
            </span>
          )}
          <Button
            variant="outline"
            onClick={handleResetAll}
          >
            Reset All to Defaults
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(sections).map(([sectionCode, section]) => (
          <Card key={sectionCode}>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              {section.name}
            </h2>
            <div className="space-y-4">
              {section.categories.map(category => {
                const currentPrompt = prompts[category.code] || category.aiPrompt
                const isEditing = editingCategory === category.code
                const isModified = currentPrompt !== category.aiPrompt

                return (
                  <div
                    key={category.code}
                    className="border border-slate-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-medium text-slate-900">
                          {category.code}: {category.name}
                        </h3>
                        {isModified && !isEditing && (
                          <span className="text-xs text-blue-600 mt-1">Modified from default</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!isEditing ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(category.code)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {isModified && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReset(category.code)}
                                className="text-orange-600 hover:text-orange-700"
                              >
                                Reset
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleSave(category.code)}
                            >
                              <Save className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancel}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {isEditing ? (
                      <textarea
                        value={editedPrompt}
                        onChange={(e) => setEditedPrompt(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none font-mono text-sm"
                        rows={4}
                        placeholder="Enter AI prompt..."
                      />
                    ) : (
                      <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded border border-slate-100 font-mono whitespace-pre-wrap">
                        {currentPrompt}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-medium text-blue-900 mb-2">💡 Tips for Writing Effective Prompts</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Be specific about what to analyze (e.g., "condition, cracks, dampness")</li>
          <li>• Request concise responses (e.g., "Respond in 2-3 sentences")</li>
          <li>• Focus on actionable observations rather than opinions</li>
          <li>• Include technical terminology relevant to RICS surveying</li>
          <li>• Prompts are saved locally and applied when analyzing photos</li>
        </ul>
      </div>
    </div>
  )
}
